import { useContext, useEffect, useState, useMemo } from "react";
import { Flex, message, Segmented, Spin, Switch } from "antd";

import NoticesSettingsTable from "@/components/tables/NoticesSettingsTable";

import { useTableAutoHeight } from "@/hooks/useTableAutoHeight";

import { UserContext } from "@/context/UserContext";

import SpeakerphoneIcon from "@/icons/SpeakerphoneIcon";

import {
  getNotificationSettings,
  setNotificationSettings,
  getNotificationTypes,
} from "@/api/notices";
import { setSoundNotificationsEnabled } from "@/api/users";

import styles from "./settings-tab.module.css";

const getTreeRules = (settings) => {
  const firstLevel = [];
  const mappingSettings = {};

  settings.forEach((setting) => {
    if (setting.is_group || !setting.parent_rule_uuid) {
      firstLevel.push(setting);
    } else if (setting.parent_rule_uuid) {
      if (!mappingSettings[setting.parent_rule_uuid])
        mappingSettings[setting.parent_rule_uuid] = [];
      mappingSettings[setting.parent_rule_uuid].push(setting);
    }
  });

  return firstLevel.map((setting) => {
    const children = mappingSettings[setting.rule_uuid];
    return {
      ...setting,
      ...(children && { children }),
    };
  });
};

const SettingsTab = () => {
  const { user, setUser } = useContext(UserContext);

  const [optionsPending, setOptionsPending] = useState(false);
  const [pending, setPending] = useState(true);

  const [settings, setSettings] = useState([]);
  const [notificationTypes, setNotificationTypes] = useState([]);

  const [selectedType, setSelectedType] = useState("all");

  const [soundPending, setSoundPending] = useState(false);
  const soundEnabled = user?.sound_notifications_enabled !== false;

  const typesOptions = useMemo(() => {
    return [
      { label: "Все", value: "all" },
      ...notificationTypes.map((type) => ({
        label: type.name,
        value: type.uuid,
      })),
    ];
  }, [notificationTypes]);

  const filteredSettings = useMemo(() => {
    return settings.filter((setting) => {
      if (selectedType === "all") return true;
      return setting.notification_type?.uuid === selectedType;
    });
  }, [settings, selectedType]);

  // Автоматическая высота таблицы
  const { tableContainerRef, tableHeight, headerHeight } = useTableAutoHeight([
    settings,
  ]);

  //  initial fetch
  useEffect(() => {
    fetchOptions();
    // eslint-disable-next-line
  }, []);

  const fetchOptions = async () => {
    setOptionsPending(true);

    try {
      const types = await getNotificationTypes();

      setNotificationTypes(types);
      fetchData();
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setOptionsPending(false);
    }
  };

  const fetchData = async () => {
    setPending(true);
    try {
      const settings = await getNotificationSettings();
      setSettings(getTreeRules(settings));
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setPending(false);
    }
  };

  const handleSoundToggle = async (checked) => {
    setSoundPending(true);

    // оптимистичное обновление с откатом при ошибке
    const prevUser = user;
    setUser({ ...user, sound_notifications_enabled: checked });

    try {
      await setSoundNotificationsEnabled(checked);
    } catch (error) {
      setUser(prevUser);
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setSoundPending(false);
    }
  };

  const handleSaveSettings = async (settings) => {
    setPending(true);
    try {
      await setNotificationSettings(settings);
      fetchData();
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setPending(false);
    }
  };

  return (
    <>
      {optionsPending ? (
        <div className="spin">
          <Spin size="large" />
        </div>
      ) : (
        <Flex vertical gap={15} className="h-100">
          <Flex
            align="center"
            justify="space-between"
            className={styles.toolbar}
          >
            <Segmented
              value={selectedType}
              options={typesOptions}
              onChange={setSelectedType}
            />

            <Flex gap={10} align="center" className={styles.soundToggle}>
              <Switch
                checked={soundEnabled}
                loading={soundPending}
                onChange={handleSoundToggle}
              />
              <SpeakerphoneIcon
                style={{ fontSize: 16, color: "var(--color-deep-blue)" }}
              />
              <span>Звуковая индикация уведомлений</span>
            </Flex>
          </Flex>

          <div
            ref={tableContainerRef}
            style={{
              flex: 1,
              minHeight: 0,
            }}
          >
            <NoticesSettingsTable
              data={filteredSettings}
              loading={pending}
              onChange={handleSaveSettings}
              scroll={{ x: "100%", y: tableHeight - headerHeight }}
            />
          </div>
        </Flex>
      )}
    </>
  );
};

export default SettingsTab;
